"use client";
import Icon from "@/components/icons/Icons";
import CustomDropdown from "@/components/ui/CustomDropdown";
import { FundOption } from "@/type/rollingPageTypes";
import { fuzzySearch } from "@/utils/commonSearchText";
import { formatDate } from "@/utils/commonFunctions";
import React, { useRef, useState } from "react";

type Props = {
  index: number;
  selected: FundOption | null;
  options: FundOption[];
  onSelect: (fund: FundOption) => void;
  onRemove: () => void;
  className?: string;
};

export default function RollingCalcDropdown({ index, selected, options, onSelect, onRemove, className }: Props) {
  const buttonRef = useRef<HTMLButtonElement>(null);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [fundSearch, setFundSearch] = useState<string>("");

  const filteredOptions = options.filter((fund) => fuzzySearch(fundSearch, fund.scheme_name ?? ""));

  return (
    <div className={`relative ${className}`} style={{ zIndex: 5 - index }}>
      <div className="flex items-center gap-2">
        <button
          ref={buttonRef}
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="w-full p-2 sm:px-4 sm:py-5 rounded-lg sm:rounded-2xl border border-gray-300 bg-white text-left text-sm flex gap-2.5 justify-between items-center"
        >
          <span className="inline-block">
            {selected?.scheme_name || `Select Fund ${index + 1}`}{" "}
            {selected?.launch_date && (
              <span className="text-xs text-gray-500">(Launch: {formatDate(selected.launch_date, "MMM yyyy")})</span>
            )}
          </span>
          <Icon.dropdownDownArrow
            className={`shrink-0 ml-auto text-primaryBlack/70 transition duration-300 ${isDropdownOpen ? "rotate-180" : ""}`}
          />
        </button>

        {index !== 0 && (
          <button
            type="button"
            onClick={onRemove}
            className="group rounded-full bg-white border border-[#cecece] p-1.5 sm:p-2 hover:border-red-400 transition"
            aria-label="Remove Fund"
          >
            <Icon.crossIcon className="w-3.5 h-3.5 xs:w-4 xs:h-4 text-[#cecece] group-hover:text-red-400 transition" />
          </button>
        )}
      </div>

      <CustomDropdown
        isOpen={isDropdownOpen}
        excludeRefs={[buttonRef]}
        onClose={() => setIsDropdownOpen(false)}
        className="mt-2 w-full z-50 shadow-md rounded-lg sm:rounded-2xl overflow-hidden p-1.5"
      >
        <label className="block rounded-xl relative mb-1">
          <Icon.searchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-[#bbbbbb] w-4 h-4" />
          <input
            type="text"
            placeholder="Search fund"
            value={fundSearch}
            onChange={(e) => setFundSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-gray-200 rounded-lg outline-hidden transition-all duration-200"
          />
        </label>

        <ul className="divide-y divide-gray-100 max-h-60 overflow-y-auto scrollbar-xs pr-1">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((fund, index) => (
              <li key={index}>
                <button
                  onClick={() => {
                    onSelect(fund);
                    setIsDropdownOpen(false);
                    setFundSearch("");
                  }}
                  className={`w-full text-left p-1.5 text-sm rounded-md ${fund.scheme_name === selected?.scheme_name ? "bg-primaryGreen/10 text-primaryGreen font-semibold" : "hover:bg-primaryGreen/5"}`}
                >
                  {fund.scheme_name}
                </button>
              </li>
            ))
          ) : (
            <li className="px-4 py-4 text-sm  text-center">No funds found</li>
          )}
        </ul>
      </CustomDropdown>
    </div>
  );
}
